import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { acceptAnchor, type AnchorAcceptResponse } from './api';

/**
 * Screen-2 anchor-accept mutation hook.
 *
 * Fires `POST /api/book/orders/:orderId/anchor/accept` with the
 * envelope's current `cover.attemptId`. On success the order
 * transitions `anchor_review → anchor_approved` (or stays
 * `anchor_approved` on the `already_accepted` idempotent path).
 *
 * The caller is responsible for calling `refetch()` on success so the
 * shell's envelope updates and BookRoute re-dispatches to the next
 * status naturally (G12: order status is the only source).
 *
 * ═══ RESULT SHAPE ════════════════════════════════════════════════════
 *
 * `accept()` resolves with a discriminated result so the caller can
 * branch on the error's `error_code` inline without waiting for a
 * re-render. The hook still stores the last `ApiError` in state for
 * downstream observers, but the result object is the immediate
 * source of truth. Necessary for the transient error routing on
 * screen 2 (state 6 vs state 7).
 */

export type AnchorAcceptResult =
  | { readonly ok: true; readonly response: AnchorAcceptResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UseAnchorAcceptState {
  readonly accepting: boolean;
  readonly error: ApiError | null;
  readonly accept: (anchorAttemptId: string) => Promise<AnchorAcceptResult>;
}

export function useAnchorAccept(orderId: string): UseAnchorAcceptState {
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const accept = useCallback(
    async (anchorAttemptId: string): Promise<AnchorAcceptResult> => {
      setAccepting(true);
      setError(null);
      try {
        const response = await acceptAnchor(orderId, anchorAttemptId);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setAccepting(false);
      }
    },
    [orderId],
  );

  return { accepting, error, accept };
}
